/**
 * PdfPreview — simple modal to preview PDF attachments from ticket messages.
 * Uses native <iframe> which every modern browser renders.
 * Fetches the blob via auth header, converts to object URL, then shows it.
 */
import React, { useEffect, useState } from 'react';
import { X, Download, Loader2, FileText, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PdfPreview({ orderId, message, token, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let revoke = null;
    let stop = false;
    (async () => {
      try {
        // inline=true → backend serves with Content-Disposition: inline + correct PDF mime
        const r = await fetch(`${API_URL}/api/orders/${orderId}/messages/${message.id}/attachment?track=false&inline=true`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        // Force application/pdf so the iframe definitely renders even if server didn't set it
        const raw = await r.blob();
        const b = raw.type === 'application/pdf' ? raw : new Blob([raw], { type: 'application/pdf' });
        if (stop) return;
        const url = URL.createObjectURL(b);
        revoke = url;
        setBlobUrl(url);
      } catch (e) {
        if (!stop) setErr(e.message);
      }
    })();
    return () => {
      stop = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [orderId, message.id, token]);

  const download = async () => {
    try {
      const r = await fetch(`${API_URL}/api/orders/${orderId}/messages/${message.id}/attachment`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const b = await r.blob();
      const url = URL.createObjectURL(b);
      const a = document.createElement('a');
      a.href = url; a.download = message.attachment.name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (_) { /* silent */ }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/75 backdrop-blur-sm p-4"
      onClick={onClose} data-testid="pdf-preview-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-6 py-3 flex items-center justify-between rounded-t-xl flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-bold truncate">{message.attachment.name}</span>
          </div>
          <div className="flex items-center gap-2">
            {blobUrl && (
              <a href={blobUrl} target="_blank" rel="noreferrer" data-testid="pdf-preview-newtab"
                className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5" /> New tab
              </a>
            )}
            <button onClick={download} data-testid="pdf-preview-download"
              className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> Download
            </button>
            <button onClick={onClose} data-testid="pdf-preview-close"
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 bg-slate-200 relative">
          {err && (
            <div className="absolute inset-0 flex items-center justify-center text-red-600 text-sm">
              Could not load PDF: {err}
            </div>
          )}
          {!err && !blobUrl && (
            <div className="absolute inset-0 flex items-center justify-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="ml-2 text-sm">Loading PDF…</span>
            </div>
          )}
          {blobUrl && (
            <object
              data={blobUrl}
              type="application/pdf"
              className="w-full h-full"
              data-testid="pdf-preview-iframe"
            >
              <embed src={blobUrl} type="application/pdf" className="w-full h-full" />
              <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-600 p-6 text-center">
                <FileText className="w-12 h-12 text-slate-400" />
                <p className="text-sm">Your browser can't display PDFs inline.</p>
                <a href={blobUrl} target="_blank" rel="noreferrer"
                  className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-bold">
                  Open PDF in new tab
                </a>
              </div>
            </object>
          )}
        </div>
      </div>
    </div>
  );
}
